<template>
    <jet-dialog-modal
        :show="modalOpen" 
        @close="$emit('close')"
        ref="modal"
    >
        <template #title>
            Import Contacts
        </template>

        <template #content>
            <form>
                <div v-if="step==3">
                    <div class="mb-2">
                        <div :class="resultState" role="alert">
                            <p v-if="resultState=='good'" class="font-bold">Your file has been processed!</p>
                            <p v-if="resultState=='warning'" class="font-bold">Your file was processed, but there were some errors.</p>
                            <p v-if="resultState=='error'" class="font-bold">Your file could not be processed.</p>
                        </div>

                        <table class="table-auto text-lg mt-12 mb-12">
                            <thead>
                                <tr>
                                <th class="px-4 py-2">Created</th>
                                <th class="px-4 py-2">Updated</th>
                                <th class="px-4 py-2">Errors</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="border px-4 py-2 text-center">{{created}}</td>
                                    <td class="border px-4 py-2 text-center">{{updated}}</td>
                                    <td class="border px-4 py-2 text-center">{{importErrorCount}}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div class='mt-5' v-if="importErrors.length > 0">
                            <div>Showing up to 10 errors:</div>
                            <ul class="errors text-red-600">
                                <li v-for="importError in importErrors">{{ importError }}</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div v-show="step==1">
                    <div class="tra__message-form flex flex-wrap relative">
                        <p class="mb-2">
                            The imported file must be an Excel or CSV file and at minimum contain a phone number.
                        </p>
                        
                        <p class="mb-2">
                            You can use <span class='font-bold'>Custom Fields</span> to add additional information to your contacts.<br />
                        </p>
                        
                        <h3 class="mb-2 block w-full">
                                <a :href="route('custom-fields.download-template')"
                                target="_blank"
                                class="text-blue-400 underline font-bold"
                            >Download your template here to get started.</a>
                        </h3>

                        <div class="w-full mb-2" data-csv>
                            <div class="mb-2">
                                <label for="importFile" class="block text-sm font-medium text-gray-700">
                                    Select a file to import
                                </label>
                                <input 
                                    type="file"
                                    id="importFile"
                                    name="importFile"
                                    class="mt-1 block w-full"
                                    accept=".csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msexcel"
                                    @change="handleFileChange"
                                />
                                <p v-if="fileError" class="mt-2 text-sm text-red-600">{{ fileError }}</p>
                            </div>
                        </div>
                        <p class="mb-2 text-gray-600">*Imports are limited to 5,000 contacts at a time.</p>
                        <div class="w-full mb-2 mt-5">
                            <label class="flex items-center">
                                <input
                                    type="checkbox" 
                                    class="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                    v-model="contactterms"
                                    value="y"
                                />
                                <span class="text-sm text-gray-600">
                                    I agree to TextSpot's
                                    <a href="https://textspot.io/terms"
                                    target="_blank"
                                    class="text-blue-400 underline" >Terms of Service &amp; Use</a>.
                                    By agreeing, I verify that I've received express consent to message contacts I'm importing.
                                </span>
                            </label>
                            
                        </div>
                    </div>
                </div>
                <div v-show="step==2">
                    <div class="text-lg font-medium mb-4">
                        Review how the headers from your file match to Contact fields
                    </div>

                    <!-- Header Row Template -->
                    <div class="grid grid-cols-12 items-center border-b mb-2 ">
                        <div class="col-span-5">
                            <div class="bg-gray-50 p-2 rounded font-bold">
                                File Header
                            </div>
                        </div>
                        
                        <div class="col-span-2 flex justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                        </div>

                        <div class="col-span-5">
                            <div class="bg-gray-50 p-2 rounded font-bold">
                                Matching Data Field
                            </div>
                        </div>
                    </div>

                    <!-- Mapping Rows -->
                    <div class="">
                        <ImportHeaderRow 
                            v-for="(header, index) in headers"
                            :key="index"
                            :left="header"
                        >
                            <template #right>
                                <multiselect
                                    v-model="mapSelection[index]"
                                    :options="customFieldOptions"
                                    :searchable="true"
                                    :clearable="true"
                                    label="label"
                                    placeholder="Select matching field"
                                    select-label=""
                                    deselect-label=""
                                    @change="handleChange($event, index, header)"
                                />
                            </template>
                        </ImportHeaderRow>
                    </div>


                </div>

                
                <!-- error message -->
                <div class="mt-2" v-if="showSubmissionError">
                    <p class="text-red-600">{{submissionError}}</p>
                </div>
            </form>
        </template>

        <template #footer>
            <div class="flex justify-between">
                <div>
                    <button v-if="!contactsImported" @click="closeModal">
                        Cancel
                    </button>
                </div>
                
                <jet-button
                    type="button"
                    @click="submitHandler"
                    :disabled="loading"
                    class="ml-3"
                >
                    <span v-if="loading" class="mr-2">
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </span>
                    {{ submitButtonText }}
                </jet-button>
            </div>
        </template>
    </jet-dialog-modal>
</template>

<script>
import JetDialogModal from "../Jetstream/DialogModal";
import JetSecondaryButton from "../Jetstream/SecondaryButton";
import JetButton from "../Jetstream/Button";
import {mapWritableState, mapActions} from 'pinia';
import contactStore from '../Stores/contacts.js';
import { useGeneralTableStore } from '@/Stores/general_table';
import ImportHeaderRow from '@/components/Contacts/ImportHeaderRow.vue';
import { map } from "lodash";

export default {
    components: { JetDialogModal, JetSecondaryButton, JetButton, ImportHeaderRow },
    name: "ContactImportModal",
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        groupTraId: {
            type: String
        },
    },
    data() {
        return {
            importErrors: [],
            importErrorCount: 0, //because only up to 10 importErrors are returned
            importComplete: false,
            errorGettingHeaders: false,
            headersProcessed: false,
            headers: [],
            fieldMapping: {},
            mapSelection: {}, // For multiselect objects
            loading: false,
            importFile: null,
            fileError: "",
            contactterms: [],
            updated: 0,
            created: 0,
            showSubmissionError: false,
            submissionError: "",
        };
    },
    methods: {
        ...mapActions(useGeneralTableStore, [
            'clearData', 'fetchData',
        ]),
        handleFileChange(event) {
            this.fileError = "";
            const file = event.target.files[0];
            
            if (!file) {
                this.importFile = null;
                return;
            }
            
            // Check file type
            const allowedTypes = [
                'text/csv',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/msexcel'
            ];
            
            if (!allowedTypes.includes(file.type) && 
                !file.name.endsWith('.csv') && 
                !file.name.endsWith('.xls') && 
                !file.name.endsWith('.xlsx')) {
                this.fileError = "Please select a valid CSV or Excel file.";
                event.target.value = null;
                this.importFile = null;
                return;
            }
            
            this.importFile = file;
        },
        closeModal() {
            this.$emit('close');
        },
        async submitHandler() {
            let formData = new FormData();
            formData.append('contactterms', this.contactterms);
            formData.append('groupTraId', this.groupTraId);
            this.showSubmissionError = false;

            if (this.step == 1) {

                if (!this.contactterms.length) {
                    this.showSubmissionError = true;
                    this.submissionError = "You must agree to the terms to continue.";
                    return;
                }

                //file must be present
                if (!this.importFile) {
                    this.showSubmissionError = true;
                    this.submissionError = "You must select a file to continue.";
                    return;
                }

                //handle the uploading of the file
                this.loading = true;
                formData.append('file', this.importFile);
                
                const response = await axios.post(route('get.headers'), formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                this.loading = false;
                if (response.data.success) {
                    this.headers = response.data.headers;
                    this.headersProcessed = true;
                    this.matchFields();
                }
                formData = null;
                return;
            }

            if (this.step == 2) {

                //we need to make sure that at least phone_number is matched
                if (!Object.values(this.fieldMapping).includes('phone_number')) {
                    this.showSubmissionError = true;
                    this.submissionError = "You must match at least the phone number field to continue.";
                    return;
                }

                //make sure the same header field doesn't map to the same column twice
                // Only check non-empty values (i.e., fields that aren't skipped)
                const values = Object.values(this.fieldMapping).filter(value => value !== "");
                const duplicates = values.filter((value, index) => values.indexOf(value) !== index);
                if (duplicates.length) {
                    this.showSubmissionError = true;
                    this.submissionError = "You cannot map the same field to multiple columns.";
                    return;
                }

                formData.append('file', this.importFile);
                formData.append('fieldMapping', JSON.stringify(this.fieldMapping));

                this.loading = true;
                axios.post(route('contacts.import'), formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }).then((response) => {
                    this.loading = false;
                    this.importErrors = response.data.example_errors;
                    this.importErrorCount = response.data.errors;
                    this.updated = response.data.updated;
                    this.created = response.data.created;
                    this.importComplete = true;
                    
                    //lets give contactsImported store variable current date/time to reflect the update
                })
                .catch(function (error) {
                    console.error(error);
                });
            }//ends step 2



            //if we already handled step 2, close
            if (this.step == 3) {
                this.fetchData(); //refresh general table
                this.contactsImported = new Date().toString();
                this.closeModal();
                return;
            }
          
        },
        handleChange(selected, index, header) {
            console.log(selected, index, header);
            if (!selected) {
                // When cleared, set to empty string (Skip)
                this.fieldMapping[index] = "";
                return;
            } 
            // Store the value in fieldMapping for form submission
            this.fieldMapping[index] = selected.value;
        },
        async blankFunction() {
            // function to stop formulate's upload from going automatically
            return new Promise(resolve => {
                resolve();
            })
        },
        normalize(str) {
            return str.toString().toLowerCase().replace(/\s+/g, '');
        },
        matchFields() {
            // Helper function to normalize strings for comparison
            // Process each header
            this.headerOptions.forEach(header => {
                const normalizedHeader = this.normalize(header.label);
                // Check for basic fields first
                if (normalizedHeader.includes('phone') || normalizedHeader.includes('number')) {
                    const option = this.customFieldOptions.find(opt => opt.value === 'phone_number');
                    this.mapSelection[header.value] = option;
                    this.fieldMapping[header.value] = 'phone_number';
                    return;
                }

                if (normalizedHeader.includes('first')) {
                    const option = this.customFieldOptions.find(opt => opt.value === 'first_name');
                    this.mapSelection[header.value] = option;
                    this.fieldMapping[header.value] = 'first_name';
                    return;
                }

                if (normalizedHeader.includes('last')) {
                    const option = this.customFieldOptions.find(opt => opt.value === 'last_name');
                    this.mapSelection[header.value] = option;
                    this.fieldMapping[header.value] = 'last_name';
                    return;
                }

                if (normalizedHeader.includes('assign') || normalizedHeader.includes('assignto') || normalizedHeader.includes('assign_to')) {
                    const option = this.customFieldOptions.find(opt => opt.value === 'assign_to');
                    this.mapSelection[header.value] = option;
                    this.fieldMapping[header.value] = 'assign_to';
                    return;
                }

                // Then check against custom fields
                const customMatch = this.customFieldOptions.find(option => {
                    // For address fields, only match if the specific part is mentioned
                    if (option.value.includes('.')) {
                        const [, addressPart] = option.value.split('.');
                        return normalizedHeader.includes(this.normalize(addressPart));
                    }
                    
                    // For regular custom fields
                    return this.normalize(option.label) === normalizedHeader;
                });

                if (customMatch) {
                    this.mapSelection[header.value] = customMatch;
                    this.fieldMapping[header.value] = customMatch.value;
                }
            });
        }, //ends matchFields function
    },
    computed: {
        ...mapWritableState(contactStore, ['contactsImported']),
        modalOpen() {
            return this.isOpen;
        },
        step() {
            if (!this.headersProcessed) return 1;
            if (!this.importComplete) return 2;
            return 3;
        },
        submitButtonText() {
            if (this.loading) return "Importing...";
            if (this.step == 1) return "Next";
            if (this.step == 2) return "Import File";
            return "Close";
        },
        resultState() {
            if (!this.importComplete) return "incomplete";
            if (!this.created && !this.updated && this.importErrorCount > 0) return "error";
            if ((this.created || this.updated) && this.importErrorCount) return "warning";
            
            return "good";
        },
        customFieldOptions() {
            const defaultFields = [
                { label: '-- Skip --', value: "" },
                { label: 'Phone Number', value: 'phone_number' },
                { label: 'First Name', value: 'first_name' },
                { label: 'Last Name', value: 'last_name' },
                { label: 'Assign To', value: 'assign_to' },
            ];

            const customFields = this.$page.props.custom_fields.data.flatMap((customField) => {
                if (customField.type === "Address") {
                    return [
                        'street1',
                        'street2',
                        'city',
                        'state',
                        'postal_code',
                        'country'
                    ].map(part => ({
                        label: `${customField.name}: ${part}`,
                        value: `${customField.tra_id}.${part}`
                    }));
                }

                return [{
                    label: customField.name,
                    value: customField.tra_id
                }];
            });

            return [...defaultFields, ...customFields];
        },
        headerOptions() {
            return this.headers.map((header, index) => {
                return {
                    label: header,
                    value: index
                };
            });
        },
    },
    watch: {
        mapSelection: {
            handler(newVal, oldVal) {
                console.log("In watch mapSelection");
                //step through each mapSelection and set fieldMapping index to value as apropriate
                for (const [index, selected] of Object.entries(newVal)) {
                    if (selected) {
                        this.fieldMapping[index] = selected.value;
                    } else {
                        this.fieldMapping[index] = "";
                    }
                }
            },
            deep: true
        },
    }
};
</script>

<style scoped>
code {
    display: block;
    padding: 1rem;
    background: #eee;
    margin-bottom: 1rem;
}

table {
    width: 100%;
    min-width: 400px;
    table-layout: fixed;
}

td {
    padding: 0.25rem;
    border: 1px dashed black;
}

thead td {
    font-weight: bold;
}

/* result classes */
.good {
    @apply bg-green-100 border-t border-b border-green-500 text-green-700 px-4 py-3;
}

.error {
    @apply bg-red-100 border-t border-b border-red-500 text-red-700 px-4 py-3;
}
.warning {
    @apply bg-yellow-100 border-t border-b border-yellow-500 text-yellow-700 px-4 py-3;
}
</style>
